/**
 * Web search specific port communication with the Salvage Yard extension.
 *
 * This module handles:
 * - Port-based communication for web search article promotion
 * - Sending WEB_SEARCH_INIT context to extension
 * - Receiving NEWS_SUBMIT from extension when user adds article
 * - Connection management with automatic reconnection
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { SalvageYardID } from "../../constants/FamaExtension";
import { getChromeApi } from "./chrome";
import { ExtensionConnectionStatus } from "./types";

/**
 * Extended status for web search bridge (includes checking state).
 */
export type WebSearchBridgeStatus = ExtensionConnectionStatus | "checking";

/**
 * Result of bridge status check.
 */
export interface WebSearchBridgeStatusResult {
  status: WebSearchBridgeStatus;
  version?: string;
  error?: string;
}

/**
 * Payload sent from extension when user clicks "Add to Report".
 */
export interface WebSearchNewsSubmitPayload {
  report_uuid: string;
  result_external_id: string;
  override_title: string;
  override_url: string;
  override_body: string;
  override_author?: string | null;
  override_date?: string | null;
}

/**
 * Person information for PII highlighting
 */
export interface PersonInfo {
  primary_name: {
    first_name: string;
    middle_name?: string;
    last_name: string;
  };
  aliases?: Array<{
    first_name: string;
    middle_name?: string;
    last_name: string;
  }>;
  birth_date?: string; // YYYY-MM-DD or similar
  locations: string[]; // Cities from addresses, jobs, schools
}

/**
 * Context sent to extension when user clicks "View" on a result.
 */
export interface WebSearchContext {
  report_uuid: string;
  result_external_id: string;
  result_url: string;
  report_label?: string;
  prefill?: {
    title?: string;
    body?: string;
    author?: string;
    date?: string;
  };
  personinfo?: PersonInfo;
}

/**
 * Hook for web search extension communication.
 *
 * Handles:
 * - Extension connection detection and ping
 * - Port-based communication with the extension
 * - Sending web search context to extension
 * - Receiving NEWS_SUBMIT from extension
 */
export function useWebSearchExtensionBridge() {
  const [status, setStatus] = useState<WebSearchBridgeStatusResult>({
    status: "checking",
  });
  const portRef = useRef<any | null>(null);
  const onSubmitRef = useRef<
    | ((
        payload: WebSearchNewsSubmitPayload,
        meta: { sourceTabId?: number }
      ) => void)
    | null
  >(null);
  const retryCountRef = useRef(0);
  const maxRetries = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReconnectingRef = useRef(false);

  // Function to attempt connection - extracted so it can be called on visibility change
  const attemptConnection = useCallback(() => {
    const chromeApi = getChromeApi();
    if (!chromeApi?.runtime?.sendMessage) {
      setStatus({
        status: "not_installed",
        error: "Chrome runtime unavailable",
      });
      return;
    }

    // Reset retry count for fresh attempt
    retryCountRef.current = 0;
    isReconnectingRef.current = true;
    setStatus({ status: "checking" });

    const attemptPing = () => {
      if (!isReconnectingRef.current) return;

      console.info("[WebSearchBridge] Attempting ping...", {
        attempt: retryCountRef.current + 1,
        maxRetries,
      });

      try {
        chromeApi.runtime.sendMessage(
          SalvageYardID,
          { action: "PING" },
          (response: any) => {
            if (!isReconnectingRef.current) return;
            if (chromeApi.runtime.lastError) {
              console.warn(
                "[WebSearchBridge] Ping failed:",
                chromeApi.runtime.lastError.message
              );
              scheduleRetry();
              return;
            }
            if (!response?.ok) {
              console.warn("[WebSearchBridge] Ping response not ok:", response);
              scheduleRetry();
              return;
            }
            const version = response?.version || "0.0.0";
            console.info("[WebSearchBridge] ✅ Ping successful", { version });
            retryCountRef.current = 0;
            isReconnectingRef.current = false;
            setStatus({ status: "connected", version });
          }
        );
      } catch (error: any) {
        console.warn("[WebSearchBridge] Ping exception:", error?.message);
        scheduleRetry();
      }
    };

    const scheduleRetry = () => {
      if (!isReconnectingRef.current) return;
      retryCountRef.current++;
      if (retryCountRef.current < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const delay = Math.min(
          1000 * Math.pow(2, retryCountRef.current - 1),
          16000
        );
        console.info("[WebSearchBridge] Scheduling retry", {
          attempt: retryCountRef.current,
          delay,
        });
        reconnectTimeoutRef.current = setTimeout(attemptPing, delay);
      } else {
        console.warn(
          "[WebSearchBridge] Max retries reached, marking as not_installed"
        );
        isReconnectingRef.current = false;
        setStatus({ status: "not_installed" });
      }
    };

    // Start first ping attempt
    attemptPing();
  }, []);

  // Initial connection attempt on mount
  useEffect(() => {
    attemptConnection();

    return () => {
      isReconnectingRef.current = false;
      if (reconnectTimeoutRef.current)
        clearTimeout(reconnectTimeoutRef.current);
    };
  }, [attemptConnection]);

  // Reconnect when page becomes visible (handles tab switching, window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Only attempt reconnect if not already connected and not already trying
        if (status.status !== "connected" && !isReconnectingRef.current) {
          console.info(
            "[WebSearchBridge] 👁️ Page became visible, attempting reconnect..."
          );
          attemptConnection();
        } else if (status.status === "connected" && !portRef.current) {
          // Connected but port lost - reconnect
          console.info(
            "[WebSearchBridge] 👁️ Page visible, port missing, reconnecting..."
          );
          attemptConnection();
        }
      }
    };

    const handleFocus = () => {
      // Also check on window focus
      if (status.status !== "connected" && !isReconnectingRef.current) {
        console.info(
          "[WebSearchBridge] 🎯 Window focused, checking connection..."
        );
        attemptConnection();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [status.status, attemptConnection]);

  // Periodic health check - every 30s, verify connection is still alive
  useEffect(() => {
    const healthCheck = setInterval(() => {
      if (status.status === "connected" && !portRef.current) {
        console.warn(
          "[WebSearchBridge] ⚠️ Health check: connected but no port, reconnecting..."
        );
        attemptConnection();
      }
    }, 30000);

    return () => clearInterval(healthCheck);
  }, [status.status, attemptConnection]);

  useEffect(() => {
    console.info("[WebSearchBridge] Port effect triggered", {
      statusStatus: status.status,
    });
    if (status.status !== "connected") {
      console.info("[WebSearchBridge] Skipping port setup - not connected", {
        status: status.status,
      });
      return;
    }
    const chromeApi = getChromeApi();
    if (!chromeApi?.runtime?.connect) {
      console.error("[WebSearchBridge] chrome.runtime.connect unavailable");
      setStatus({ status: "connection_failed", error: "connect unavailable" });
      return;
    }

    try {
      console.info(
        "[WebSearchBridge] Establishing port connection to extension...",
        { extensionId: SalvageYardID }
      );
      const port = chromeApi.runtime.connect(SalvageYardID, {
        name: "web-search-ui",
      });
      portRef.current = port;
      console.info("[WebSearchBridge] ✅ Port connected successfully");

      port.onMessage.addListener((message: any) => {
        // Filter out heartbeat logs - they're too noisy
        if (message?.action !== "HEARTBEAT") {
          console.info("[WebSearchBridge] 📥 Message received on port", {
            action: message?.action,
            ok: message?.ok,
            hasData: !!message?.data,
          });
        }

        // Handle HEARTBEAT - just acknowledge the connection is alive
        if (message?.action === "HEARTBEAT") {
          // Heartbeat received - connection is alive, nothing to do
          return;
        }

        // Handle NEWS_SUBMIT (user clicked Add to Report in extension modal)
        if (message?.action === "NEWS_SUBMIT" && message?.data) {
          console.info(
            "[WebSearchBridge] 📥 NEWS_SUBMIT received - auto-promoting",
            {
              report_uuid: message.data?.report_uuid,
              result_external_id: message.data?.result_external_id,
              override_title: message.data?.override_title?.slice(0, 50),
            }
          );
          onSubmitRef.current?.(message.data, {
            sourceTabId: message?.meta?.sourceTabId,
          });
        }

        // Handle SCRAPER_READY (extension connected and ready)
        if (message?.action === "SCRAPER_READY") {
          console.info("[WebSearchBridge] 📥 Extension ready", {
            connectedAt: message?.data?.connectedAt,
            connectionId: message?.data?.connectionId,
            extensionVersion: message?.data?.extensionVersion,
          });
        }
      });

      port.onDisconnect.addListener(() => {
        console.warn("[WebSearchBridge] ⚠️ Port disconnected", {
          lastError: chromeApi?.runtime?.lastError?.message,
        });
        portRef.current = null;

        // Clear any pending reconnection timeout
        if (disconnectTimeoutRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }

        // Don't just give up - schedule a reconnection attempt after a short delay
        // This handles extension updates, temporary disconnects, etc.
        console.info(
          "[WebSearchBridge] 🔄 Scheduling reconnection after disconnect..."
        );
        disconnectTimeoutRef.current = setTimeout(() => {
          disconnectTimeoutRef.current = null;
          if (!portRef.current && !isReconnectingRef.current) {
            console.info(
              "[WebSearchBridge] 🔄 Attempting reconnection after disconnect..."
            );
            attemptConnection();
          }
        }, 2000); // Wait 2s before reconnecting (extension might be reloading)
      });

      // Cleanup function: disconnect port on unmount or when effect re-runs
      return () => {
        // Clear any pending reconnection timeout
        if (disconnectTimeoutRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }

        if (portRef.current) {
          console.info("[WebSearchBridge] 🧹 Cleaning up port connection");
          try {
            portRef.current.disconnect();
          } catch (e) {
            // Ignore errors - port may already be disconnected
          }
          portRef.current = null;
        }
      };
    } catch (error: any) {
      console.error("[WebSearchBridge] ❌ Port connection failed", {
        error: error?.message,
      });
      setStatus({
        status: "connection_failed",
        error: error?.message || "Failed to connect",
      });
    }
  }, [status.status, attemptConnection]);

  // Manual reconnect function for external use (e.g., after extension install)
  const reconnect = useCallback(() => {
    console.info("[WebSearchBridge] 🔄 Manual reconnect triggered");
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    isReconnectingRef.current = false;
    attemptConnection();
  }, [attemptConnection]);

  /**
   * Send web search context to extension when user clicks "View" on a result.
   * Returns true if sent successfully, false if no port connection.
   */
  const sendWebSearchContext = useCallback(
    (payload: WebSearchContext): boolean => {
      // Use status from state at call time, not closure
      const currentStatus = status.status;
      console.info("[WebSearchBridge] 📤 sendWebSearchContext called", {
        report_uuid: payload.report_uuid,
        result_external_id: payload.result_external_id,
        result_url: payload.result_url?.slice(0, 80),
        hasPort: !!portRef.current,
        statusStatus: currentStatus,
      });

      // If no port but we think we're connected, try to reconnect first
      if (!portRef.current && currentStatus === "connected") {
        console.warn(
          "[WebSearchBridge] ⚠️ Port missing despite connected status, will reconnect"
        );
        // Don't block - just warn. The context might be sent after reconnect
      }

      if (!portRef.current) {
        console.warn(
          "[WebSearchBridge] ❌ Cannot send context - no port connected"
        );
        return false;
      }
      portRef.current.postMessage({ type: "WEB_SEARCH_INIT", payload });
      console.info("[WebSearchBridge] ✅ WEB_SEARCH_INIT sent to extension");
      return true;
    },
    [status.status]
  );

  /**
   * Send response back to extension after processing NEWS_SUBMIT.
   */
  const sendSubmitResponse = useCallback(
    (payload: { tabId?: number; ok: boolean; reason?: string }) => {
      if (!portRef.current) return;
      const type = payload.ok
        ? "NEWS_SUBMIT_CONFIRMED"
        : "NEWS_SUBMIT_REJECTED";
      portRef.current.postMessage({
        type,
        payload: { tabId: payload.tabId, reason: payload.reason },
      });
    },
    []
  );

  /**
   * Set the callback for when NEWS_SUBMIT is received from extension.
   */
  const setOnSubmit = useCallback((handler: typeof onSubmitRef.current) => {
    onSubmitRef.current = handler;
  }, []);

  return {
    status,
    sendWebSearchContext,
    sendSubmitResponse,
    setOnSubmit,
    reconnect,
  };
}
